'use client'

import { type ReactNode, useLayoutEffect } from 'react'
import { useAppDispatch, useInjectPageReducer } from '@app/model/store/client'
import PageLayout from '@app/PageLayout/PageLayout'

import { initNewsItem } from '@pages/NewsItem/model/actions'
import NewsItemReducer, { resetState } from '@pages/NewsItem/model/slice'
import NewsItem from '@pages/NewsItem'

import { BlockBoundary } from '@shared/lib/components/BlockBoundary'
import type { TypeMappingNewsOutput } from '@shared/api/middleware/mappingAPI/news/type'
import type { TypeMappingRestOutput } from '@shared/api/middleware/mappingAPI/restaurant/type'
import type { TypeGlobalOutput } from '@shared/api/mappingGlobalProps'
import type { BreadcrumbItem } from '@shared/ui/Navigation/Breadcrumbs'

const CURRENT_PAGE = 'news_item'

export type NewsItemPageClientProps = {
  global_data: TypeGlobalOutput
  data: {
    news: TypeMappingNewsOutput
    other_news?: TypeMappingNewsOutput[] | null
  }
  breadcrumbItems: BreadcrumbItem[]
  seo_restaurants?: TypeMappingRestOutput[] | null
  slug: string
  children?: ReactNode
}

export default function NewsItemPageClient({
  data,
  global_data,
  seo_restaurants,
  slug,
  breadcrumbItems,
  children,
}: NewsItemPageClientProps) {
  useInjectPageReducer('news_item', NewsItemReducer)

  const { news, other_news } = data
  const dispatch = useAppDispatch()

  // карточки часто с scroll={false} — без этого после перехода остаётся скролл с прошлой страницы
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [slug])

  useLayoutEffect(() => {
    dispatch(initNewsItem({ news, other_news: other_news ?? [], restaurants: seo_restaurants ?? [] }))

    return () => {
      dispatch(resetState())
    }
  }, [slug, dispatch, news?.id, seo_restaurants?.length])

  return (
    <BlockBoundary>
      <PageLayout globalData={global_data} currentPage={CURRENT_PAGE} breadcrumbItems={breadcrumbItems}>
        <NewsItem
          serverArticle={children}
          initialNews={news}
          initialRestaurants={seo_restaurants ?? null}
          initialOthers={other_news ?? null}
        />
      </PageLayout>
    </BlockBoundary>
  )
}
